import React, { useState, useEffect, useRef } from 'react';
import './AdminPanel.css';
import API_URL, { parseJsonResponse } from '../api';
import { TL_ACCOUNT_OPTIONS, ACCOUNT_DREAME } from '../constants/accountOptions';

function TLAccounts() {
  const [tlList, setTlList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editName, setEditName] = useState("");
  const [editCreatedAt, setEditCreatedAt] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [editRfid, setEditRfid] = useState("");
  const [editEid, setEditEid] = useState("");
  const [editAccount, setEditAccount] = useState(ACCOUNT_DREAME);
  const [photoUploadTargetId, setPhotoUploadTargetId] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const photoInputRef = useRef(null);

  useEffect(() => {
    fetchTLs();
  }, []);

  const fetchTLs = async () => {
    try {
      const response = await fetch(`${API_URL}?action=get_tls`);
      const data = await parseJsonResponse(response);
      if (data?.status === 'success') {
        setTlList(Array.isArray(data.tls) ? data.tls : []);
      } else {
        console.error('TL fetch failed:', data.message || data);
        setTlList([]);
      }
    } catch (error) {
      console.error('Error fetching TLs:', error);
      setTlList([]);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Gusto mo bang burahin ang account ni ${name}?`)) {
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'delete_tl', id: id })
        });
        const data = await parseJsonResponse(response);
        if (data?.status === 'success') {
          fetchTLs();
          alert("TL Account Deleted!");
        }
      } catch (err) {
        alert("Server Error");
      }
    }
  };

  const startEdit = (tl) => {
    setEditingId(tl.id);
    setEditUsername(tl.username);
    setEditPassword("");
    setEditName(tl.full_name);
    setEditCreatedAt(tl.created_at);
    setEditPosition(tl.position);
    setEditRfid(tl.rfid || "");
    setEditEid(tl.eid || "");
    setEditAccount(tl.account || ACCOUNT_DREAME);
  };

  const cancelEdit = () => setEditingId(null);

  const handlePhotoClick = (tl) => {
    if (isUploadingPhoto) return;
    setPhotoUploadTargetId(tl.id);
    photoInputRef.current?.click();
  };

  const handlePhotoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !photoUploadTargetId) return;

    if (!file.type.startsWith('image/')) {
      alert('Pili lang ng image file.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const photoBase64 = reader.result;
      setIsUploadingPhoto(true);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update_tl_photo',
            id: photoUploadTargetId,
            photo: photoBase64,
          }),
        });
        const data = await parseJsonResponse(response);
        if (data?.status === 'success') {
          setTlList((prev) => prev.map((tl) => (
            tl.id === photoUploadTargetId ? { ...tl, photo: photoBase64 } : tl
          )));
          await fetchTLs();
          alert('Na-update na ang litrato.');
        } else {
          alert(data.message || 'Hindi na-update ang litrato.');
        }
      } catch (error) {
        console.error('TL photo update error:', error);
        alert('Server error. Subukan ulit.');
      } finally {
        setIsUploadingPhoto(false);
        setPhotoUploadTargetId(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_tl',
          id: editingId,
          username: editUsername,
          password: editPassword,
          full_name: editName,
          created_at: editCreatedAt,
          position: editPosition,
          rfid: editRfid,
          eid: editEid,
          account: editAccount,
        })
      });
      const data = await parseJsonResponse(response);
      if (data?.status === 'success') {
        setEditingId(null);
        fetchTLs();
        alert("TL Account Updated!");
      } else {
        alert("Error: " + data.message);
      }
    } catch (err) {
      alert("Server Error");
    }
  };

  return (
    <div className="admin-panel admin-panel--wide">
      <div className="admin-panel-header">
        <div className="admin-panel-header-left">
          <div className="admin-panel-badge">
            <span className="admin-panel-badge-dot" />
            Accounts
          </div>
          <h2 className="admin-panel-title">TL Accounts List</h2>
          <p className="admin-panel-subtitle">{tlList.length} team leader{tlList.length !== 1 ? 's' : ''} registered</p>
        </div>
      </div>

      <div className="admin-table-wrap">
        <input
          type="file"
          ref={photoInputRef}
          accept="image/*"
          onChange={handlePhotoFileChange}
          style={{ display: 'none' }}
        />
        {tlList.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <p className="admin-empty-title">No Team Leaders registered yet</p>
            <p className="admin-empty-sub">Register a new TL from the Register TL page.</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Username</th>
                <th>Password</th>
                <th>Full Name</th>
                <th>UID</th>
                <th>EID</th>
                <th>Created At</th>
                <th>Position</th>
                <th>Account</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tlList.map((tl) => (
                <React.Fragment key={tl.id}>
                  <tr>
                    <td>
                      <button
                        type="button"
                        className="admin-photo-change-btn admin-photo-change-btn--editable"
                        onClick={() => handlePhotoClick(tl)}
                        disabled={isUploadingPhoto}
                        title="Update your Photo"
                      >
                        <img
                          src={tl.photo || 'https://via.placeholder.com/40'}
                          alt={tl.full_name}
                          className="admin-table-photo"
                        />
                        <span className="admin-photo-change-overlay" aria-hidden="true">📷</span>
                      </button>
                    </td>
                    <td><span className="admin-table-name">{tl.username}</span></td>
                    <td><span className="admin-table-muted">••••••••</span></td>
                    <td>{tl.full_name}</td>
                    <td><span className="admin-table-muted">{tl.rfid || 'N/A'}</span></td>
                    <td><span className="admin-table-muted">{tl.eid || 'N/A'}</span></td>
                    <td>{tl.created_at}</td>
                    <td>{tl.position}</td>
                    <td><span className="admin-table-muted">{tl.account || 'DREAME'}</span></td>
                    <td>
                      <div className="admin-btn-group" style={{ justifyContent: 'center' }}>
                        <button onClick={() => startEdit(tl)} className="admin-btn admin-btn--edit">Edit</button>
                        <button onClick={() => handleDelete(tl.id, tl.full_name)} className="admin-btn admin-btn--delete">Delete</button>
                      </div>
                    </td>
                  </tr>

                  {editingId === tl.id && (
                    <tr className="admin-edit-row">
                      <td colSpan="10">
                        <form onSubmit={handleUpdate} className="admin-edit-form">
                          <input type="text" className="admin-form-input" style={{ flex: 1, minWidth: 140 }} placeholder="Username" value={editUsername} onChange={e => setEditUsername(e.target.value)} required />
                          <input type="password" className="admin-form-input" style={{ flex: 1, minWidth: 140 }} placeholder="New Password (optional)" value={editPassword} onChange={e => setEditPassword(e.target.value)} />
                          <input type="text" className="admin-form-input" style={{ flex: 1, minWidth: 160 }} placeholder="Full Name" value={editName} onChange={e => setEditName(e.target.value)} required />
                          <input type="text" className="admin-form-input" style={{ flex: 1, minWidth: 130 }} placeholder="RFID UID" value={editRfid} onChange={e => setEditRfid(e.target.value)} />
                          <input type="text" className="admin-form-input" style={{ flex: 1, minWidth: 130 }} placeholder="EID" value={editEid} onChange={e => setEditEid(e.target.value)} />
                          <input type="date" className="admin-form-input" style={{ flex: 1, minWidth: 130 }} value={editCreatedAt} onChange={e => setEditCreatedAt(e.target.value)} required />
                          <select className="admin-form-input" style={{ flex: 1, minWidth: 140 }} value={editPosition} onChange={e => setEditPosition(e.target.value)} required>
                            <option value="Team Leader">Team Leader</option>
                            <option value="Senior Team Leader">Senior Team Leader</option>
                          </select>
                          <select className="admin-form-input" style={{ flex: 1, minWidth: 120 }} value={editAccount} onChange={e => setEditAccount(e.target.value)} required>
                            {TL_ACCOUNT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <button type="submit" className="admin-btn admin-btn--save">Save</button>
                          <button type="button" onClick={cancelEdit} className="admin-btn admin-btn--cancel">Cancel</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default TLAccounts;
